/**
 * TabContext - Thin wrapper around tabStore for React context compatibility.
 *
 * This context:
 * 1. Binds tab actions to the current active project
 * 2. Handles persistence to/from server via useUserPreferencesStore
 * 3. Provides the familiar useTabs() hook API
 */
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import type { TabItem, TabContextType } from '../types';
import type { ProjectTabState } from '../services/api';
import { useTabStore, useProjectTabs } from '../stores/tabStore';
import { useLocation } from 'react-router-dom';
import { agentIdFromPath } from '../hooks/useAgentScope';
import { useProject } from './ProjectContext';
import { useUserPreferencesStore } from '../hooks/useUserPreferences';
import { homeAgentId, useHomeAgentId, useAgentsStore } from '../stores/agentsStore';

const TabContext = createContext<TabContextType | undefined>(undefined);

interface TabProviderProps {
  children: ReactNode;
}

export function TabProvider({ children }: TabProviderProps) {
  // Tabs are an agent page's: each agent keeps its own strip, keyed by its
  // id. A route that names no agent shows the strip of the agent the project
  // opens on.
  const { activeProject } = useProject();
  const location = useLocation();
  const home = useHomeAgentId(activeProject?.id);
  const tabKey = agentIdFromPath(location.pathname) ?? home ?? undefined;
  const projectTabs = useProjectTabs(tabKey);
  const initializedRef = useRef(false);

  // Load tabs from preferences on init
  useEffect(() => {
    if (initializedRef.current) return;

    const unsubscribe = useUserPreferencesStore.subscribe((state) => {
      if (state.isInitialized && !initializedRef.current) {
        initializedRef.current = true;
        useTabStore.getState().loadFromPreferences(state.preferences.tabsByProject);
      }
    });

    // Check if already initialized
    const state = useUserPreferencesStore.getState();
    if (state.isInitialized && !initializedRef.current) {
      initializedRef.current = true;
      useTabStore.getState().loadFromPreferences(state.preferences.tabsByProject);
    }

    return unsubscribe;
  }, []);

  // Persist tabs to preferences when they change (debounced)
  const persistTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tabsByProject = useTabStore((state) => state.tabsByProject);

  // A strip saved under a project's id was the one agent it opened on; it
  // moves to that agent's own key once the project's agents have answered.
  const agentsByProject = useAgentsStore((s) => s.byProject);
  useEffect(() => {
    if (!initializedRef.current) return;
    const store = useTabStore.getState();
    for (const key of Object.keys(tabsByProject)) {
      const home = key in agentsByProject ? homeAgentId(agentsByProject[key]) : null;
      if (home) store.rekey(key, home);
    }
  }, [tabsByProject, agentsByProject]);

  useEffect(() => {
    if (!initializedRef.current) return;

    // Skip write-back of data we just loaded from preferences
    if (useTabStore.getState()._loadedFromPreferences) {
      useTabStore.setState({ _loadedFromPreferences: false });
      return;
    }

    if (persistTimeoutRef.current) {
      clearTimeout(persistTimeoutRef.current);
    }

    persistTimeoutRef.current = setTimeout(() => {
      // Convert to preferences format
      const prefsFormat: Record<string, ProjectTabState> = {};
      for (const [projectId, state] of Object.entries(tabsByProject)) {
        prefsFormat[projectId] = {
          tabs: state.tabs.map((t) => ({
            id: t.id,
            itemId: t.itemId,
            itemType: t.itemType,
            title: t.title,
            isActive: t.isActive,
            isDraft: t.isDraft,
            storeKey: t.storeKey,
          })),
          activeTabId: state.activeTabId || undefined,
        };
      }

      useUserPreferencesStore.getState().updatePreferences({
        tabsByProject: prefsFormat,
      });
    }, 500);

    return () => {
      if (persistTimeoutRef.current) {
        clearTimeout(persistTimeoutRef.current);
      }
    };
  }, [tabsByProject]);

  // Create context value with bound actions
  const contextValue: TabContextType = {
    tabs: projectTabs.tabs,
    activeTab: projectTabs.activeTab,
    openTab: projectTabs.openTab as (item: TabItem) => string | undefined,
    closeTab: projectTabs.closeTab as (tabId: string) => void,
    setActiveTab: projectTabs.setActiveTab as (tabId: string) => void,
    clearActiveTab: projectTabs.clearActiveTab as () => void,
    updateTabTitle: projectTabs.updateTabTitle as (itemId: string, newTitle: string) => void,
    openDraftTab: projectTabs.openDraftTab as (title?: string) => string,
    closeDraftTab: projectTabs.closeDraftTab as () => void,
    convertDraftToTab: projectTabs.convertDraftToTab as (itemId: string, title?: string) => void,
  };

  return <TabContext.Provider value={contextValue}>{children}</TabContext.Provider>;
}

export function useTabs(): TabContextType {
  const context = useContext(TabContext);
  if (context === undefined) {
    throw new Error('useTabs must be used within a TabProvider');
  }
  return context;
}
